import { useDrag } from "react-dnd";
import { faComment } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useMemo } from "react";

import { useSelector } from "react-redux";

import { t } from "i18next";

import { clearMarkdownAndHTMLAndTrim } from "@/utils/clearMarkdownAndHTMLAndTrim";
import { Icon } from "@/components";

import { tasksSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import { commentsSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks-activity-slice";

import ToDoTag from "./ToDoTag";
import { ToDoStatus } from "../enum/ToDoStatus.enum";
import {
	TaskInfo,
	SubTaskInfo,
	Assignee,
} from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";

interface ToDoItemProps {
	data: TaskInfo | SubTaskInfo;
	showTaskDetails: (taskId: number) => void;
	users: Assignee[] | null;
	isSubTask?: boolean;
	showParentLabel?: boolean;
	isLastSubTask?: boolean;
	isOrphanGroup?: boolean;
	hasSubTasks?: boolean;
	isSubTasksCollapsed?: boolean;
	onToggleSubTasks?: () => void;
	subTasksCount?: number;
}

export default function ToDoItem({
	data,
	showTaskDetails,
	users,
	isSubTask = false,
	isOrphanGroup = false,
	showParentLabel = false,
	isLastSubTask = true,
	hasSubTasks = false,
	isSubTasksCollapsed = false,
	onToggleSubTasks,
	subTasksCount = 0,
}: ToDoItemProps) {
	const [, drag] = useDrag({
		type: isSubTask ? "TODO_SUB_ITEM" : "TODO_ITEM",
		item: isSubTask
			? { subTaskId: data.id, status: data.status, taskId: (data as SubTaskInfo).taskId }
			: { taskId: data.id, status: data.status },
	});

	const isDone = data?.status === ToDoStatus.DONE;

	const allComments = useSelector(commentsSelector);

	const commentsCount = useMemo(
		() =>
			allComments.filter((c) => (data.isTask ? c.taskId === data.id : c.subtaskId === data.id))
				.length,
		[allComments, data.id, data.isTask]
	);

	const tasks = useSelector(tasksSelector);

	const assignedUser = useMemo(() => {
		const assignee = users?.find((user) => user.id === data?.assigneeId);

		return assignee ? `${assignee.firstName} ${assignee.lastName}`.trim() : null;
	}, [users, data?.assigneeId]);

	const parentTaskTitle = !data.isTask
		? tasks.find((task) => task.id === (data as SubTaskInfo).taskId)?.title
		: null;

	const ts = (key: string) => t(`createTask.${key}`);

	return (
		<div
			className={`w-full flex flex-col gap-1 text-ellipsis    overflow-hidden  ${
				!isLastSubTask && isSubTask ? "border-b border-gray-200 " : ""
			}`}
		>
			{isSubTask && showParentLabel && parentTaskTitle && isOrphanGroup ? (
				<p className="self-start text-[14px] text-primary-blue font-medium text-ellipsis  max-w-[250px]  overflow-hidden  pl-1">
					{ts("subtaskOf")} {parentTaskTitle}
				</p>
			) : null}
			<button
				ref={drag}
				className={`w-full bg-white text-left flex gap-3 ${
					isSubTask ? "rounded-lg px-3 py-2  " : "rounded-xl p-4"
				} ${!isOrphanGroup && parentTaskTitle ? "w-[94%] self-center" : ""}`}
				style={{ boxShadow: "0px 1px 2px 0px #1C274C1F" }}
				type="button"
				onClick={() => data?.id !== undefined && showTaskDetails(data.id)}
			>
				<div className="flex-1 min-w-0">
					<div className="flex flex-col gap-1">
						{data?.tag?.name && (
							<div className="flex items-center justify-between gap-2">
								<ToDoTag className="truncate min-w-0 flex-1 max-w-fit" title={data.tag.name} />
								{commentsCount && commentsCount > 0 ? (
									<div className="flex items-center gap-1 shrink-0 text-[#777D8D]">
										<Icon icon={faComment} />
										<span className="text-[11px] font-medium">{commentsCount}</span>
									</div>
								) : null}
							</div>
						)}

						<div className="flex items-center justify-between gap-2 w-full">
							<h4
								className={`text-primary-blue-dark font-semibold max-w-[250px]  text-ellipsis text-nowrap overflow-hidden ${
									isSubTask ? "text-[12px]" : "text-[14px]"
								} ${isDone ? "line-through" : ""}`}
							>
								{data?.title}
							</h4>
							<div className="flex items-center gap-2 shrink-0">
								{!data?.tag?.name && commentsCount && commentsCount > 0 ? (
									<div className="flex items-center gap-1 text-[#777D8D]">
										<Icon icon={faComment} />
										<span className="text-[11px] font-medium">{commentsCount}</span>
									</div>
								) : null}
								{!isSubTask && hasSubTasks && (
									<span
										aria-label={
											isSubTasksCollapsed ? ts("expandSubtasks") : ts("collapseSubtasks")
										}
										className="flex items-center gap-1 cursor-pointer text-[#777D8D] hover:text-primary-blue-dark px-1.5 py-0.5 rounded hover:bg-gray-100"
										role="button"
										tabIndex={0}
										onClick={(e) => {
											e.stopPropagation();
											onToggleSubTasks?.();
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												e.stopPropagation();
												onToggleSubTasks?.();
											}
										}}
									>
										<span className="text-[11px] font-medium">{subTasksCount}</span>
										<Icon icon={isSubTasksCollapsed ? faChevronRight : faChevronDown} />
									</span>
								)}
							</div>
						</div>

						{data?.description && (
							<p
								className={`text-ellipsis overflow-hidden leading-4 ${
									isSubTask
										? "text-[11px] text-primary-blue-dark"
										: "text-[12px] text-[#777D8D]"
								} ${isDone ? "line-through" : ""}`}
							>
								{clearMarkdownAndHTMLAndTrim(data.description, isSubTask ? 80 : 120)}
							</p>
						)}

						<div className="flex items-center gap-2">
							{/* {data.priority === ToDoPriority.HIGH && (
								<Icon className="text-status-error w-4 h-4" icon={faChevronDoubleUp} />
							)}
							{data.priority === ToDoPriority.MEDIUM && (
								<Icon className="text-status-warning w-4 h-4" icon={faGripLines} />
							)}
							{data.priority === ToDoPriority.LOW && (
								<Icon className="text-blue-500 w-4 h-4" icon={faChevronDoubleDown} />
							)} */}
							{assignedUser && (
								<div className="ml-auto overflow-hidden max-w-full">
									<ToDoTag className="inline-block max-w-full truncate" title={assignedUser} />
								</div>
							)}
						</div>
					</div>
				</div>
			</button>
		</div>
	);
}
